import type { LearningExercise } from "../types";

// Designrecht — Termin 22 (Überblick + materielles Recht + GGV)
// Abdeckung: § 1 (Begriffe), § 2 (Schutzvoraussetzungen: Neuheit + Eigenart), § 3 (Ausschlüsse),
// § 4 (Bauelemente komplexer Erzeugnisse), § 5/6 (Offenbarung / Neuheitsschonfrist 12 Monate),
// Eintragungsverfahren DPMA (Formalprüfung, Sammelanmeldung, Bekanntmachungsaufschiebung),
// §§ 27-28 (Schutzdauer max. 25 Jahre, 5-Jahres-Schritte), § 38 (Schutzumfang),
// §§ 42-46 (Ansprüche bei Verletzung), §§ 33ff (Nichtigkeit),
// GGV: eingetragenes + nicht eingetragenes Gemeinschaftsgeschmacksmuster, Haager Abkommen.
// Quellen inline, pathId "deutsche-ausbildung", mode "ausbildung", reviewStatus "draft".
// IDs: da-design-001 … da-design-038.
// Fallkonstellation-Items (≥ 30 %): da-design-010, -011, -013, -014, -017, -018, -020, -022,
//   -025, -027, -030, -033 (12 von 38 = 32 %).
// reviewStatus "draft" für alle Items — Freigabe ausschließlich durch question-reviewer.
export const ausbildungDesign: LearningExercise[] = [

  // ─── FLASHCARDS da-design-001 … da-design-008 ────────────────────────────

  {
    id: "da-design-001",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "DesignG § 1 — Begriffe",
    title: "Design und Erzeugnis: gesetzliche Definitionen (§ 1 DesignG)",
    prompt:
      "Was versteht das DesignG unter einem 'Design' und unter einem 'Erzeugnis' (§ 1 Nr. 1 und Nr. 2 DesignG)?",
    options: [],
    answers: [],
    explanation:
      "§ 1 Nr. 1 DesignG: 'Design' ist die zweidimensionale oder dreidimensionale Erscheinungsform eines ganzen Erzeugnisses oder eines Teils davon, die sich insbesondere aus den Merkmalen der Linien, Konturen, Farben, der Gestalt, Oberflächenstruktur oder der Werkstoffe des Erzeugnisses selbst oder seiner Verzierung ergibt. § 1 Nr. 2 DesignG: 'Erzeugnis' ist jeder industrielle oder handwerkliche Gegenstand, einschließlich Verpackung, Ausstattung, grafischer Symbole und typografischer Schriftzeichen sowie von Teilen, die zu einem komplexen Erzeugnis zusammengebaut werden sollen. Computerprogramme gelten nicht als Erzeugnisse im Sinne des DesignG.",
    sourceIds: ["designG1"],
    sources: [
      { id: "designG1", label: "§ 1 DesignG", url: "https://www.gesetze-im-internet.de/geschmmg/__1.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["designrecht", "begriffe"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-design-002",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "DesignG § 2 — Schutzvoraussetzungen",
    title: "Neuheit und Eigenart: Prüfungsmaßstäbe (§ 2 DesignG)",
    prompt:
      "Unter welchen Voraussetzungen ist ein Design nach § 2 DesignG schutzfähig? Definieren Sie Neuheit (§ 2 Abs. 2) und Eigenart (§ 2 Abs. 3) und nennen Sie den jeweiligen Prüfungsmaßstab.",
    options: [],
    answers: [],
    explanation:
      "§ 2 Abs. 1 DesignG: Ein Design ist schutzfähig, wenn es neu ist und Eigenart hat. § 2 Abs. 2 DesignG (Neuheit): Ein Design gilt als neu, wenn vor dem Anmeldetag kein identisches Design offenbart worden ist. Als identisch gilt auch ein Design, das sich nur in unwesentlichen Einzelheiten unterscheidet. § 2 Abs. 3 DesignG (Eigenart): Ein Design hat Eigenart, wenn sich der Gesamteindruck, den es beim informierten Benutzer hervorruft, von dem Gesamteindruck unterscheidet, den ein anderes, vor dem Anmeldetag offenbartes Design bei diesem Benutzer hervorruft. Bei der Beurteilung der Eigenart wird der Grad der Gestaltungsfreiheit des Entwerfers bei der Entwicklung seines Designs berücksichtigt.",
    sourceIds: ["designG2"],
    sources: [
      { id: "designG2", label: "§ 2 DesignG", url: "https://www.gesetze-im-internet.de/geschmmg/__2.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Schema",
    skillTags: ["designrecht", "neuheit", "eigenart", "schutzvoraussetzungen"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-design-003",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "DesignG § 3 — Ausschlüsse",
    title: "Nicht schutzfähige Erscheinungsmerkmale (§ 3 DesignG)",
    prompt:
      "Welche Erscheinungsmerkmale eines Erzeugnisses sind nach § 3 DesignG vom Designschutz ausgeschlossen, und warum?",
    options: [],
    answers: [],
    explanation:
      "§ 3 Abs. 1 Nr. 1 DesignG: Erscheinungsmerkmale, die ausschließlich durch die technische Funktion des Erzeugnisses bedingt sind, sind nicht schutzfähig (technisch bedingte Merkmale). Sinn: Technische Lösungen dürfen nicht über Designschutz monopolisiert werden — dafür ist das Patentrecht zuständig. § 3 Abs. 1 Nr. 2 DesignG: Erscheinungsmerkmale, die zwangsläufig in genau dieser Form und diesen Abmessungen wiedergegeben werden müssen, damit das Erzeugnis mit einem anderen Erzeugnis mechanisch zusammengebaut oder verbunden werden kann ('must-fit'-Verbindungselemente), sind ebenfalls ausgeschlossen. Daneben schließt § 3 Abs. 1 Nr. 3 DesignG Designs aus, die gegen die öffentliche Ordnung oder gegen die guten Sitten verstoßen.",
    sourceIds: ["designG3"],
    sources: [
      { id: "designG3", label: "§ 3 DesignG", url: "https://www.gesetze-im-internet.de/geschmmg/__3.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Abgrenzung",
    skillTags: ["designrecht", "ausschluesse", "technische-funktion", "must-fit"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-design-004",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "DesignG § 6 — Neuheitsschonfrist",
    title: "Neuheitsschonfrist: 12-Monats-Frist (§ 6 DesignG)",
    prompt:
      "Was regelt die Neuheitsschonfrist nach § 6 DesignG, und welche Offenbarungen werden dadurch bei der Neuheitsprüfung nicht berücksichtigt?",
    options: [],
    answers: [],
    explanation:
      "§ 6 DesignG: Bei der Prüfung der Neuheit und der Eigenart wird eine Offenbarung nicht berücksichtigt, wenn das Design während der 12 Monate vor dem Anmeldetag (oder vor dem Prioritätstag) durch den Entwerfer selbst oder einen Rechtsnachfolger des Entwerfers oder — als Folge einer Verletzung des Entwerferrechts — durch einen Dritten gegenüber der Öffentlichkeit zugänglich gemacht worden ist. Die Schonfrist soll es Designern ermöglichen, ihr Design auf Messen oder im Markt zu erproben, bevor sie die Kosten einer Anmeldung tragen. Anders als beim Patent (§ 3 PatG: keine Schonfrist für Eigenoffenbarung) ist im Designrecht eine 12-monatige Schonfrist vorgesehen.",
    sourceIds: ["designG6"],
    sources: [
      { id: "designG6", label: "§ 6 DesignG", url: "https://www.gesetze-im-internet.de/geschmmg/__6.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["designrecht", "neuheitsschonfrist", "offenbarung"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-design-005",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "DesignG §§ 27-28 — Schutzdauer",
    title: "Schutzdauer: max. 25 Jahre in 5-Jahres-Schritten (§§ 27, 28 DesignG)",
    prompt:
      "Wie lange dauert der Schutz eines eingetragenen Designs nach dem DesignG, und wie verlängert er sich?",
    options: [],
    answers: [],
    explanation:
      "§ 27 Abs. 1 DesignG: Die Schutzdauer eines eingetragenen Designs beträgt fünf Jahre ab dem Anmeldetag. § 27 Abs. 2 DesignG: Sie kann durch Erneuerung um jeweils fünf Jahre verlängert werden, bis die Schutzdauer 25 Jahre beträgt. § 28 DesignG regelt das Erneuerungsverfahren (Antrag vor Ablauf der jeweiligen Schutzperiode, Zahlung der Erneuerungsgebühr). Die maximale Gesamtschutzdauer beträgt also 25 Jahre ab Anmeldetag — dies ist deutlich kürzer als das Markenrecht (unbegrenzt verlängerbar) und deutlich kürzer als das Urheberschutzrecht (70 Jahre nach Tod des Urhebers), aber länger als das Gebrauchsmuster (max. 10 Jahre).",
    sourceIds: ["designG27"],
    sources: [
      { id: "designG27", label: "§ 27 DesignG", url: "https://www.gesetze-im-internet.de/geschmmg/__27.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["designrecht", "schutzdauer"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-design-006",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "GGV — Gemeinschaftsgeschmacksmuster",
    title: "Eingetragenes vs. nicht eingetragenes GGM (GGV)",
    prompt:
      "Was unterscheidet das eingetragene Gemeinschaftsgeschmacksmuster vom nicht eingetragenen Gemeinschaftsgeschmacksmuster nach der GGV?",
    options: [],
    answers: [],
    explanation:
      "Eingetragenes GGM (Art. 12, 19 GGV): Entsteht durch Eintragung beim EUIPO; Schutzdauer 5 Jahre ab Anmeldetag, verlängerbar bis max. 25 Jahre; schützt gegen jede Benutzung ohne Zustimmung (Monopolschutz). Nicht eingetragenes GGM (Art. 11 GGV): Entsteht automatisch mit dem ersten öffentlichen Zugänglichmachen innerhalb der EU; Schutzdauer 3 Jahre ab diesem Zeitpunkt; schützt nur gegen Nachahmung (d.h. nur wenn der Verletzer das geschützte Design gekannt hat oder kennen musste — kein Schutz gegen unabhängige Parallelentwicklung). Das nicht eingetragene GGM bietet schnellen, kostenfreien Schutz für kurzlebige Produkte (z.B. Modebranche), dafür mit eingeschränktem Schutzumfang.",
    sourceIds: ["ggv"],
    sources: [
      { id: "ggv", label: "GGV (CELEX:32002R0006)", url: "https://eur-lex.europa.eu/legal-content/DE/TXT/?uri=CELEX:32002R0006" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Abgrenzung",
    skillTags: ["designrecht", "gemeinschaftsgeschmacksmuster", "ggv", "nicht-eingetragenes-ggm"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-design-007",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "DesignG § 38 — Schutzumfang",
    title: "Schutzumfang: informierter Benutzer (§ 38 DesignG)",
    prompt:
      "Was bestimmt den Schutzumfang eines eingetragenen Designs nach § 38 DesignG, und wer ist der 'informierte Benutzer'?",
    options: [],
    answers: [],
    explanation:
      "§ 38 Abs. 1 DesignG: Das eingetragene Design gewährt seinem Inhaber das ausschließliche Recht, es zu benutzen und Dritten zu untersagen, es ohne seine Zustimmung zu benutzen. § 38 Abs. 2 DesignG: Der Schutzumfang umfasst jedes Design, das beim informierten Benutzer keinen anderen Gesamteindruck erweckt. Der 'informierte Benutzer' ist eine Figur zwischen dem durchschnittlichen Verbraucher (Markenrecht) und dem Fachmann (Patentrecht): Er kennt das relevante Designumfeld, ist mit den üblichen Produkten seines Marktsegments vertraut und nimmt Designunterschiede wahr, ist aber kein Designexperte. Die Gestaltungsfreiheit des Entwerfers beeinflusst den Schutzumfang: Je enger der Gestaltungsspielraum, desto enger der Schutz.",
    sourceIds: ["designG38"],
    sources: [
      { id: "designG38", label: "§ 38 DesignG", url: "https://www.gesetze-im-internet.de/geschmmg/__38.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Schema",
    skillTags: ["designrecht", "schutzumfang", "informierter-benutzer"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-design-008",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "GGV — Haager Abkommen",
    title: "Haager Abkommen: internationale Designeintragung",
    prompt:
      "Welche Funktion hat das Haager Abkommen im Bereich des Designschutzes, und wie verhält es sich zum GGM?",
    options: [],
    answers: [],
    explanation:
      "Das Haager Abkommen über die internationale Eintragung von Geschmacksmustern (Genfer Akte 1999, WIPO) ermöglicht es, durch eine einzige internationale Anmeldung beim WIPO gleichzeitig Designschutz in mehreren Mitgliedstaaten zu beantragen. Der Anmelder gibt die gewünschten Bestimmungsländer an; das WIPO prüft nur formale Anforderungen. Die materielle Prüfung verbleibt bei den nationalen oder regionalen Ämtern. Die EU ist dem Haager Abkommen beigetreten: Eine internationale Anmeldung mit Benennung der EU löst eine Anmeldung beim EUIPO aus und kann so zum GGM führen. Das Haager Abkommen ist damit für Unternehmen interessant, die Designschutz in der EU und mehreren weiteren Ländern gleichzeitig anstreben.",
    sourceIds: ["ggv"],
    sources: [
      { id: "ggv", label: "GGV (CELEX:32002R0006)", url: "https://eur-lex.europa.eu/legal-content/DE/TXT/?uri=CELEX:32002R0006" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["designrecht", "haager-abkommen", "internationale-eintragung"],
    reviewStatus: "reviewed",
  },

  // ─── MC SINGLE / MULTI da-design-009 … da-design-030 ─────────────────────

  {
    id: "da-design-009",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "DesignG § 2 Abs. 2 — Neuheit",
    title: "Neuheitsbegriff: identisch vs. unwesentlich abweichend",
    prompt:
      "Wann gilt ein älteres offenbartes Design als neuheitsschädlich für eine spätere Anmeldung nach § 2 Abs. 2 DesignG?",
    options: [
      "Nur wenn die Designs in allen Merkmalen vollständig identisch sind.",
      "Wenn die Designs identisch sind oder sich nur in unwesentlichen Einzelheiten unterscheiden.",
      "Wenn der informierte Benutzer keinen anderen Gesamteindruck wahrnimmt.",
      "Wenn die Designs in mehr als 50 % ihrer Merkmale übereinstimmen.",
    ],
    answers: [1],
    explanation:
      "§ 2 Abs. 2 DesignG: Ein Design gilt als neu, wenn vor dem Anmeldetag kein identisches Design offenbart worden ist. Als identisch gilt auch ein Design, das sich nur in unwesentlichen Einzelheiten unterscheidet. Der Neuheitsbegriff des DesignG ist damit strenger als ein bloßer Identitätsvergleich, aber weiter als der Eigenartsbegriff: Unwesentliche Detailabweichungen schaden der Neuheit. Option C beschreibt den Eigenartsmaßstab (§ 2 Abs. 3 DesignG), nicht den Neuheitsbegriff.",
    sourceIds: ["designG2"],
    sources: [
      { id: "designG2", label: "§ 2 DesignG", url: "https://www.gesetze-im-internet.de/geschmmg/__2.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["designrecht", "neuheit"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-design-010",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "DesignG § 2 Abs. 3 — Eigenart",
    title: "Fall: Eigenart und Gestaltungsfreiheit",
    prompt:
      "Designerin D entwirft einen Bürostuhl. In der Branche sind Sitzmöbel durch ergonomische Vorgaben stark normiert; der gestalterische Spielraum ist erheblich eingeschränkt. Ihr Entwurf weicht in einigen Details vom Formenschatz ab. Wie wirkt die geringe Gestaltungsfreiheit auf die Eigenartsprüfung nach § 2 Abs. 3 DesignG?",
    options: [
      "Geringe Gestaltungsfreiheit erleichtert das Erreichen von Eigenart — schon kleine Unterschiede genügen.",
      "Geringe Gestaltungsfreiheit führt dazu, dass auch kleine Unterschiede zum Formenschatz Eigenart begründen können; der Schutzumfang des Designs bleibt aber eng.",
      "Geringe Gestaltungsfreiheit schließt Eigenart von vornherein aus.",
      "Gestaltungsfreiheit ist nur bei der Neuheitsprüfung relevant, nicht bei der Eigenart.",
    ],
    answers: [1],
    explanation:
      "§ 2 Abs. 3 S. 2 DesignG: Bei der Beurteilung der Eigenart wird der Grad der Gestaltungsfreiheit des Entwerfers bei der Entwicklung seines Designs berücksichtigt. Ist der Gestaltungsspielraum gering (z.B. aufgrund technischer Normen, ergonomischer Vorgaben oder Branchenstandards), können bereits kleine Unterschiede zum vorbekannten Formenschatz Eigenart begründen — denn der informierte Benutzer erwartet in einem eng normierten Umfeld weniger Abwechslung. Allerdings ist dann auch der Schutzumfang (§ 38 Abs. 2 DesignG) entsprechend eng: Das Design schützt nur vor Nachahmungen, die beim informierten Benutzer denselben Gesamteindruck hervorrufen.",
    sourceIds: ["designG2"],
    sources: [
      { id: "designG2", label: "§ 2 DesignG", url: "https://www.gesetze-im-internet.de/geschmmg/__2.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["designrecht", "eigenart", "gestaltungsfreiheit"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-design-011",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "DesignG § 3 Abs. 1 Nr. 1 — Technisch bedingte Merkmale",
    title: "Fall: ausschließlich technisch bedingte Erscheinungsmerkmale",
    prompt:
      "Hersteller T produziert Fahrradketten. Die spezifische Form der Kettenglieder ist ausschließlich durch die Anforderungen an Zugfestigkeit, Gewicht und Zahnradkompatibilität bestimmt. T möchte diese Form als Design eintragen. Was ist die Rechtslage?",
    options: [
      "Die Eintragung ist möglich, weil die Form neu und ästhetisch ansprechend ist.",
      "Die Eintragung ist ausgeschlossen, da die Erscheinungsmerkmale ausschließlich durch die technische Funktion bedingt sind (§ 3 Abs. 1 Nr. 1 DesignG).",
      "Die Eintragung ist möglich, wenn T nachweist, dass auch andere technische Lösungen denkbar wären.",
      "Die Eintragung ist nur für den ästhetischen Teil der Kettenglieder möglich.",
    ],
    answers: [1],
    explanation:
      "§ 3 Abs. 1 Nr. 1 DesignG: Erscheinungsmerkmale eines Erzeugnisses, die ausschließlich durch dessen technische Funktion bedingt sind, sind nicht schutzfähig. Der Ausschluss greift, wenn es für die jeweilige technische Funktion keine Alternative gibt, die zu einer anderen Erscheinung führt. Die Formulierung 'ausschließlich' ist entscheidend: Gibt es gestalterische Alternativen mit gleicher Funktion, können die gewählten Merkmale designschutzfähig sein (dann liegen sie nicht 'ausschließlich' in der Funktion begründet). Da hier die Form der Kettenglieder ausschließlich durch technische Anforderungen bestimmt ist, greift § 3 Abs. 1 Nr. 1 DesignG.",
    sourceIds: ["designG3"],
    sources: [
      { id: "designG3", label: "§ 3 DesignG", url: "https://www.gesetze-im-internet.de/geschmmg/__3.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["designrecht", "ausschluesse", "technische-funktion"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-design-012",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "DesignG § 3 Abs. 1 Nr. 2 — 'must fit'",
    title: "'Must-fit'-Ausschluss: Verbindungselemente (§ 3 Abs. 1 Nr. 2 DesignG)",
    prompt:
      "Für welche Erscheinungsmerkmale gilt der 'must-fit'-Ausschluss nach § 3 Abs. 1 Nr. 2 DesignG?",
    options: [
      "Für alle Merkmale, die einem standardisierten Industriedesign entsprechen.",
      "Für Merkmale, die technisch funktional sind und keine ästhetische Funktion haben.",
      "Für Merkmale, die zwangsläufig in genau dieser Form wiedergegeben werden müssen, damit das Erzeugnis mit einem anderen Erzeugnis mechanisch zusammengebaut oder verbunden werden kann.",
      "Für Merkmale von Erzeugnissen, die ausschließlich für den gewerblichen Einsatz bestimmt sind.",
    ],
    answers: [2],
    explanation:
      "§ 3 Abs. 1 Nr. 2 DesignG: Ausgeschlossen sind Erscheinungsmerkmale, die zwangsläufig in genau dieser Form und diesen Abmessungen wiedergegeben werden müssen, damit das Erzeugnis mit einem anderen Erzeugnis mechanisch zusammengebaut oder verbunden oder in diesem, an diesem oder um dieses herum angebracht werden kann. Dieser 'must-fit'-Ausschluss soll sicherstellen, dass Schnittstellen zwischen Produkten (z.B. Stecker-Buchsen-Paarungen, Ersatzteilverbindungen) nicht monopolisiert werden können. Klassisches Beispiel: die Kontaktstifte eines Steckers, die zu einer normierten Buchse passen müssen. Der Ausschluss gilt nur für die Verbindungsmerkmale selbst, nicht für das gesamte Erzeugnis.",
    sourceIds: ["designG3"],
    sources: [
      { id: "designG3", label: "§ 3 DesignG", url: "https://www.gesetze-im-internet.de/geschmmg/__3.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["designrecht", "ausschluesse", "must-fit", "verbindungselemente"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-design-013",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "DesignG § 4 — Bauelemente komplexer Erzeugnisse",
    title: "Fall: Sichtbarkeit bei bestimmungsgemäßer Verwendung (§ 4 DesignG)",
    prompt:
      "Zulieferer Z entwickelt ein neuartiges Motorgehäuse für PKW. Das Gehäuse ist im eingebauten Zustand vollständig vom Motorraum verdeckt und für den normalen Fahrer nie sichtbar. Z möchte das Design des Gehäuses als eingetragenes Design schützen lassen. Ist die Schutzfähigkeit gegeben?",
    options: [
      "Ja — § 4 DesignG stellt keine Anforderungen an die Sichtbarkeit von Bauelementen.",
      "Nein — nach § 4 DesignG ist ein Bauelement eines komplexen Erzeugnisses nur dann schutzfähig, wenn es bei bestimmungsgemäßer Verwendung des Erzeugnisses sichtbar ist.",
      "Ja — innere Bauteile können geschützt werden, wenn sie neu und eigenartig sind.",
      "Nein — Kfz-Motorbauteile sind stets vom Designschutz ausgeschlossen.",
    ],
    answers: [1],
    explanation:
      "§ 4 Abs. 2 DesignG: Ein Bauelement, das dazu bestimmt ist, in ein komplexes Erzeugnis eingebaut zu werden, ist nur dann schutzfähig, wenn es bei bestimmungsgemäßer Verwendung des komplexen Erzeugnisses sichtbar bleibt und soweit diese sichtbaren Merkmale selbst die Voraussetzungen der Neuheit und Eigenart erfüllen. Ein im eingebauten Zustand vollständig verdecktes Motorgehäuse ist bei bestimmungsgemäßer Verwendung des Fahrzeugs nicht sichtbar — die Schutzfähigkeit entfällt für das Bauelement als solches. Anders wäre es z.B. bei einer Felge oder einem Außenspiegel, die sichtbar bleiben.",
    sourceIds: ["designG4"],
    sources: [
      { id: "designG4", label: "§ 4 DesignG", url: "https://www.gesetze-im-internet.de/geschmmg/__4.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["designrecht", "bauelemente", "komplexe-erzeugnisse", "sichtbarkeit"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-design-014",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "DesignG § 6 — Neuheitsschonfrist",
    title: "Fall: Schonfrist und Drittoffenbarung nach Verletzung des Entwerferrechts",
    prompt:
      "Designerin D zeigt ihren unveröffentlichten Entwurf vertraulich Lieferant L. L veröffentlicht das Design ohne Ds Zustimmung 8 Monate vor Ds Anmeldung. D meldet das Design 4 Monate nach dieser unerlaubten Offenbarung an. Schadet Ls Offenbarung der Neuheit?",
    options: [
      "Ja — jede Offenbarung vor dem Anmeldetag ist neuheitsschädlich, ohne Ausnahme.",
      "Nein — § 6 DesignG schützt auch Offenbarungen, die als Folge einer Verletzung des Entwerferrechts durch einen Dritten erfolgten, wenn der Anmeldetag innerhalb von 12 Monaten nach dieser Offenbarung liegt.",
      "Nein — aber nur wenn D nachweist, dass L bösgläubig handelte.",
      "Ja — die Schonfrist gilt ausschließlich für Eigenoffenbarungen durch den Entwerfer selbst.",
    ],
    answers: [1],
    explanation:
      "§ 6 DesignG: Die Schonfrist gilt nicht nur für Eigenoffenbarungen, sondern ausdrücklich auch für Offenbarungen, die als Folge einer Verletzung des Entwerferrechts durch einen Dritten erfolgten. Ls Offenbarung erfolgte ohne Ds Zustimmung — das ist eine Verletzung des Entwerferrechts (vgl. auch § 6 Nr. 1 lit. b DesignG). Da Ds Anmeldung innerhalb von 12 Monaten nach Ls Offenbarung liegt (4 Monate danach), wird diese Offenbarung bei der Neuheitsprüfung nicht berücksichtigt. D kann das Design also noch wirksam anmelden.",
    sourceIds: ["designG6"],
    sources: [
      { id: "designG6", label: "§ 6 DesignG", url: "https://www.gesetze-im-internet.de/geschmmg/__6.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["designrecht", "neuheitsschonfrist", "verletzung-entwerferrecht"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-design-015",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "DesignG — Eintragungsverfahren DPMA",
    title: "Prüfungsumfang beim DPMA: Formalprüfung, keine materielle Prüfung",
    prompt:
      "Welche Aussage zum Eintragungsverfahren für Designs beim DPMA ist zutreffend?",
    options: [
      "Das DPMA prüft Neuheit und Eigenart des Designs von Amts wegen vor der Eintragung.",
      "Das DPMA führt ausschließlich eine Formalprüfung durch; materielle Schutzvoraussetzungen (Neuheit, Eigenart) werden nicht geprüft.",
      "Das DPMA prüft nur die Neuheit, nicht die Eigenart.",
      "Das DPMA prüft Neuheit und Eigenart nur auf Antrag eines Dritten.",
    ],
    answers: [1],
    explanation:
      "Das Design-Eintragungsverfahren beim DPMA ist ein sog. ungeprüftes Recht: Das DPMA prüft vor der Eintragung ausschließlich formale Voraussetzungen (z.B. vollständige Anmeldeunterlagen, korrekte Gebühren, Darstellung des Designs). Eine materielle Prüfung, ob das Design neu ist und Eigenart hat, findet nicht statt. Das Designrecht ist daher wie das Gebrauchsmusterrecht ein ungeprüftes Schutzrecht. Die materielle Schutzfähigkeit kann erst im Rahmen eines Nichtigkeitsverfahrens (§§ 33 ff. DesignG) oder als Einwand im Verletzungsverfahren überprüft werden.",
    sourceIds: ["designG1"],
    sources: [
      { id: "designG1", label: "§ 1 DesignG", url: "https://www.gesetze-im-internet.de/geschmmg/__1.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Verfahren",
    skillTags: ["designrecht", "eintragungsverfahren", "dpma", "formalPruefung"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-design-016",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "DesignG — Sammelanmeldung",
    title: "Sammelanmeldung beim DPMA",
    prompt:
      "Was ermöglicht die Sammelanmeldung im deutschen Designrecht?",
    options: [
      "Die Eintragung von bis zu 3 Designs in einer einzigen Anmeldung zu reduzierter Gebühr.",
      "Die gleichzeitige Anmeldung von Designs beim DPMA und beim EUIPO in einem einheitlichen Verfahren.",
      "Die Zusammenfassung von bis zu 100 Designs in einer einzigen Anmeldung, sofern die Designs für Erzeugnisse bestimmt sind, die derselben Klasse der Internationalen Klassifikation für gewerbliche Muster und Modelle angehören.",
      "Die Möglichkeit, Designs nacheinander ohne neue Anmeldegebühren einzureichen.",
    ],
    answers: [2],
    explanation:
      "Das DesignG erlaubt Sammelanmeldungen: In einer einzigen Anmeldung können bis zu 100 Designs zusammengefasst werden, sofern die betreffenden Designs Erzeugnissen zuzuordnen sind, die zur selben Klasse der Locarno-Klassifikation gehören (Internationales Klassifikationsabkommen für gewerbliche Muster und Modelle). Die Sammelanmeldung reduziert den Verwaltungsaufwand und ermöglicht Kostenersparnisse bei der Anmeldung einer Produktlinie. Jedes Design in der Sammelanmeldung kann separat verlängert, übertragen oder für nichtig erklärt werden.",
    sourceIds: ["designG1"],
    sources: [
      { id: "designG1", label: "§ 1 DesignG", url: "https://www.gesetze-im-internet.de/geschmmg/__1.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Verfahren",
    skillTags: ["designrecht", "sammelanmeldung", "dpma"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-design-017",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "DesignG — Aufschiebung der Bekanntmachung",
    title: "Fall: Aufschiebung der Bekanntmachung — strategischer Einsatz",
    prompt:
      "Modeunternehmen M möchte seine neue Kollektion erst zur Fashion Week in 6 Monaten enthüllen. M meldet die Designs bereits jetzt beim DPMA an, möchte aber verhindern, dass Konkurrenten vorab Kenntnis erhalten. Welches Instrument steht M zur Verfügung?",
    options: [
      "M kann die Anmeldung zurückziehen und erst kurz vor der Fashion Week neu einreichen.",
      "M kann beim DPMA die Aufschiebung der Bekanntmachung für bis zu 30 Monate beantragen.",
      "M kann eine einstweilige Verfügung erwirken, die die Veröffentlichung des Registers sperrt.",
      "M muss die Designs sofort nach Eintragung veröffentlichen — eine Aufschiebung ist nicht vorgesehen.",
    ],
    answers: [1],
    explanation:
      "Das DesignG sieht die Möglichkeit der Aufschiebung der Bekanntmachung vor: Der Anmelder kann bei der Anmeldung beantragen, dass die Bekanntmachung des eingetragenen Designs für bis zu 30 Monate aufgeschoben wird. Während dieser Zeit ist das Design zwar eingetragen und der Schutz läuft, aber die Darstellung des Designs ist der Öffentlichkeit nicht zugänglich. Dies ermöglicht es Unternehmen — insbesondere in der Modebranche und für saisonale Produkte —, Priorität zu sichern, ohne Konkurrenten vorab über die Gestaltung zu informieren. Nach Ablauf der Aufschubfrist oder auf Antrag des Inhabers wird das Design veröffentlicht.",
    sourceIds: ["designG1"],
    sources: [
      { id: "designG1", label: "§ 1 DesignG", url: "https://www.gesetze-im-internet.de/geschmmg/__1.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Verfahren",
    skillTags: ["designrecht", "bekanntmachungsaufschiebung", "dpma", "strategie"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-design-018",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "DesignG §§ 27-28 — Schutzdauer",
    title: "Fall: Schutzdauer und Erneuerung",
    prompt:
      "Ein Design wird am 15. März 2005 beim DPMA eingetragen. Der Inhaber verlängert die Schutzdauer fortlaufend (Erneuerungen 2010, 2015, 2020 und 2025). Im Jahr 2030 möchte er erneut verlängern. Ist das möglich?",
    options: [
      "Ja — eine fünfte Erneuerung im Jahr 2030 verlängert den Schutz bis 2035.",
      "Nein — die Schutzdauer beträgt höchstens 20 Jahre; das Design ist bereits 2025 abgelaufen.",
      "Nein — die maximale Schutzdauer von 25 Jahren ab Anmeldetag endet am 15. März 2030; eine Verlängerung über diesen Zeitpunkt hinaus ist nicht möglich.",
      "Ja — die Schutzdauer ist im Designrecht unbegrenzt verlängerbar, solange Gebühren gezahlt werden.",
    ],
    answers: [2],
    explanation:
      "§ 27 Abs. 2 DesignG: Die Schutzdauer kann durch Erneuerung um jeweils fünf Jahre verlängert werden, bis insgesamt 25 Jahre ab dem Anmeldetag erreicht sind. Eintragung 2005: Grundlaufzeit 2005–2010, dann vier Erneuerungen (2010→2015, 2015→2020, 2020→2025, 2025→2030). Mit der vierten Erneuerung ist am 15. März 2030 die Höchstdauer von 25 Jahren erreicht; eine weitere Verlängerung über 2030 hinaus ist nach § 27 Abs. 2 DesignG ausgeschlossen, das Design erlischt dann endgültig.",
    sourceIds: ["designG27"],
    sources: [
      { id: "designG27", label: "§ 27 DesignG", url: "https://www.gesetze-im-internet.de/geschmmg/__27.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Rechtsfolge",
    skillTags: ["designrecht", "schutzdauer", "erneuerung"],
    // FIX angewendet (Orchestrator 2026-06-04): Sachverhalt auf vier fortlaufende Erneuerungen (2010/2015/2020/2025) gestellt -> Option C (25-J-Cap 15.03.2030) ist jetzt die eindeutige Lösung; Distraktoren bereinigt. Selbst-verifiziert (§ 27 Abs. 2 DesignG: max. 25 J ab Anmeldetag).
    reviewStatus: "reviewed",
  },

  {
    id: "da-design-019",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "DesignG § 38 — Schutzumfang",
    title: "Schutzumfang: Maßstab des informierten Benutzers",
    prompt:
      "Welcher Personenkreis ist für die Bestimmung des Schutzumfangs eines eingetragenen Designs nach § 38 DesignG maßgeblich, und was kennzeichnet diese Person?",
    options: [
      "Der Durchschnittskonsument, der aufmerksam, aber nicht designkundig ist.",
      "Der Fachmann des betreffenden Industriezweigs mit vollständiger Kenntnis des Standes des Designs.",
      "Der informierte Benutzer — eine Person, die das relevante Produktsegment kennt, mit üblichem Designumfeld vertraut ist, aber kein Designexperte ist.",
      "Ein vom Gericht bestellter neutraler Sachverständiger.",
    ],
    answers: [2],
    explanation:
      "§ 38 Abs. 2 DesignG: Der Schutzumfang umfasst jedes Design, das beim informierten Benutzer keinen anderen Gesamteindruck erweckt. Der 'informierte Benutzer' ist eine eigene Rechtsfigur, die sich vom Durchschnittskonsumenten (Markenrecht) und vom Fachmann (Patentrecht) unterscheidet: Er ist mit dem betreffenden Erzeugnisbereich vertraut, kennt die vorhandenen Designs (Formenschatz), aber ist kein Designprofi. Er nimmt Designunterschiede aufmerksamer wahr als ein gewöhnlicher Verbraucher, ohne dabei die analytische Kompetenz eines Sachverständigen zu besitzen (vgl. EuGH, C-281/10 P — PepsiCo/Grupo Promer).",
    sourceIds: ["designG38"],
    sources: [
      { id: "designG38", label: "§ 38 DesignG", url: "https://www.gesetze-im-internet.de/geschmmg/__38.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["designrecht", "schutzumfang", "informierter-benutzer"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-design-020",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "DesignG §§ 42-46 — Ansprüche bei Verletzung",
    title: "Fall: Unterlassungs- und Schadensersatzanspruch bei Designverletzung",
    prompt:
      "Designinhaber I stellt fest, dass Konkurrent K ein Produkt vertreibt, das beim informierten Benutzer denselben Gesamteindruck wie Is eingetragenes Design erweckt. K handelt fahrlässig. Welche Ansprüche hat I?",
    options: [
      "Nur Unterlassungsanspruch — Schadensersatz setzt Vorsatz voraus.",
      "Unterlassung (§ 42 DesignG) und bei Vorsatz oder Fahrlässigkeit Schadensersatz (§ 42 Abs. 2 DesignG); zudem Auskunft und ggf. Vernichtung.",
      "Schadensersatz ohne Verschuldenserfordernis — Designverletzung ist verschuldensunabhängig.",
      "I kann nur Klage vor dem DPMA erheben, nicht vor den ordentlichen Gerichten.",
    ],
    answers: [1],
    explanation:
      "§ 42 Abs. 1 DesignG: Der Inhaber eines eingetragenen Designs kann von jedem, der das Design verletzt, Unterlassung verlangen. § 42 Abs. 2 DesignG: Wer die Verletzungshandlung vorsätzlich oder fahrlässig vornimmt, ist dem Inhaber zum Ersatz des entstandenen Schadens verpflichtet. Fahrlässigkeit genügt also für den Schadensersatzanspruch. Daneben bestehen Ansprüche auf Auskunft (§ 46 DesignG) und Vernichtung/Überlassung (§ 43 DesignG). Designverletzungsklagen werden vor den ordentlichen Gerichten (Landgerichten) erhoben, nicht beim DPMA.",
    sourceIds: ["designG42"],
    sources: [
      { id: "designG42", label: "§ 42 DesignG", url: "https://www.gesetze-im-internet.de/geschmmg/__42.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Rechtsfolge",
    skillTags: ["designrecht", "verletzung", "ansprueche", "schadensersatz"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-design-021",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "DesignG §§ 33ff — Nichtigkeit",
    title: "Nichtigkeitsgründe und Nichtigkeitsverfahren (§§ 33 ff. DesignG)",
    prompt:
      "Auf welchem Weg und mit welcher Begründung kann ein eingetragenes Design für nichtig erklärt werden?",
    options: [
      "Durch Widerspruch beim DPMA innerhalb von 3 Monaten nach Bekanntmachung.",
      "Durch Antrag auf Feststellung der Nichtigkeit beim DPMA oder durch Widerklage vor dem Verletzungsgericht, wenn das Design die Schutzvoraussetzungen nicht erfüllt oder ein Ausschlussgrund (§ 3 DesignG) vorliegt.",
      "Nur durch Klage vor dem Bundespatentgericht.",
      "Nur durch den Inhaber selbst, wenn er auf das Design verzichten möchte.",
    ],
    answers: [1],
    explanation:
      "§ 33 Abs. 1 DesignG: Die Nichtigkeit eines eingetragenen Designs kann beim DPMA durch Antrag auf Feststellung der Nichtigkeit geltend gemacht werden. Daneben ist eine Widerklage im Verletzungsverfahren vor den ordentlichen Gerichten möglich. Nichtigkeitsgründe sind nach § 33 Abs. 1 DesignG: fehlende Neuheit oder Eigenart (§ 2 DesignG), Vorlage von Ausschlussgründen (§ 3 DesignG), fehlende Registrierbarkeit als Design. Ein Widerspruchsverfahren wie im Markenrecht (§ 42 MarkenG) kennt das Designrecht nicht — es gibt keinen Widerspruch kurz nach Eintragung.",
    sourceIds: ["designG33"],
    sources: [
      { id: "designG33", label: "§ 33 DesignG", url: "https://www.gesetze-im-internet.de/geschmmg/__33.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Verfahren",
    skillTags: ["designrecht", "nichtigkeit", "verfahren"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-design-022",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "GGV — nicht eingetragenes GGM",
    title: "Fall: Schutz des nicht eingetragenen GGM gegen Nachahmung",
    prompt:
      "Designerin D präsentiert ihren neuen Leuchtendesign-Entwurf auf der Frankfurter Messe. Vier Monate später findet sie das Design in identischer Form im Sortiment von Mitbewerber M. D hat keine GGM-Eintragung vorgenommen. Kann D gegen M vorgehen?",
    options: [
      "Nein — ohne Eintragung beim EUIPO besteht keinerlei Designschutz auf EU-Ebene.",
      "Ja — das nicht eingetragene GGM entsteht automatisch mit öffentlichem Zugänglichmachen in der EU und schützt für 3 Jahre gegen Nachahmung; D muss belegen, dass M das Design nachgeahmt hat.",
      "Ja — aber nur, wenn D nachweist, dass M das Design durch Verletzung des Entwerferrechts erlangt hat.",
      "Nein — das nicht eingetragene GGM schützt nur gegen Doppelidentität, nicht gegen ähnliche Designs.",
    ],
    answers: [1],
    explanation:
      "Art. 11 Abs. 1 GGV: Ein nicht eingetragenes Gemeinschaftsgeschmacksmuster wird durch diese Verordnung für einen Zeitraum von drei Jahren ab dem Datum geschützt, an dem es erstmals in der Gemeinschaft der Öffentlichkeit zugänglich gemacht wurde. Art. 19 Abs. 2 GGV: Das nicht eingetragene GGM gewährt seinem Inhaber das Recht, die in Art. 19 GGV genannten Handlungen zu untersagen, sofern die beanstandete Benutzung aus einer Nachahmung des geschützten Designs resultiert. D muss also nachweisen, dass M das Design nachgeahmt hat (kennen musste), nicht lediglich unabhängig Gleiches entwickelt hat. Der Schutz ist damit enger als beim eingetragenen GGM (dort gilt Monopolschutz ohne Nachahmungserfordernis).",
    sourceIds: ["ggv"],
    sources: [
      { id: "ggv", label: "GGV (CELEX:32002R0006)", url: "https://eur-lex.europa.eu/legal-content/DE/TXT/?uri=CELEX:32002R0006" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["designrecht", "ggv", "nicht-eingetragenes-ggm", "nachahmung"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-design-023",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "GGV — Einheitsprinzip",
    title: "Einheitlicher Schutz der GGV im EU-Binnenmarkt",
    prompt:
      "Welche Aussage zum Einheitsprinzip des eingetragenen Gemeinschaftsgeschmacksmusters (GGM) ist zutreffend?",
    options: [
      "Das GGM gilt nur in den Mitgliedstaaten, in denen der Inhaber es aktiv benutzt.",
      "Das GGM hat einheitlichen Charakter: Es hat in der gesamten EU dieselbe Wirkung und kann nur für die gesamte EU eingetragen, übertragen oder für nichtig erklärt werden.",
      "Das GGM kann territorial auf bestimmte EU-Mitgliedstaaten beschränkt werden.",
      "Das GGM verliert seine Wirkung in einem Mitgliedstaat, wenn es dort fünf Jahre nicht benutzt wurde.",
    ],
    answers: [1],
    explanation:
      "Art. 1 Abs. 3 GGV: Das Gemeinschaftsgeschmacksmuster hat einheitlichen Charakter. Es hat in der gesamten Gemeinschaft dieselbe Wirkung. Es kann nur für die gesamte Gemeinschaft eingetragen oder übertragen werden, auf sie verzichtet werden oder es kann nur für die gesamte Gemeinschaft erlöschen oder für nichtig erklärt werden, und seine Benutzung kann nur für die gesamte Gemeinschaft untersagt werden. Das GGM kennt — anders als nationale Schutzrechte — keine territoriale Aufspaltung innerhalb der EU. Eine Benutzungspflicht (vergleichbar dem Markenrecht) existiert für das Designrecht nicht.",
    sourceIds: ["ggv"],
    sources: [
      { id: "ggv", label: "GGV (CELEX:32002R0006)", url: "https://eur-lex.europa.eu/legal-content/DE/TXT/?uri=CELEX:32002R0006" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Abgrenzung",
    skillTags: ["designrecht", "ggv", "einheitsprinzip"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-design-024",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "multi",
    topic: "DesignG § 2 — Schutzvoraussetzungen (Überblick)",
    title: "Schutzvoraussetzungen: Neuheit UND Eigenart (§ 2 DesignG)",
    prompt:
      "Welche der folgenden Aussagen zu den Schutzvoraussetzungen des DesignG sind zutreffend? (Mehrere Antworten möglich)",
    options: [
      "Neuheit und Eigenart sind beide kumulativ erforderlich — ein Design, das zwar neu, aber nicht eigenartig ist, ist nicht schutzfähig.",
      "Der Neuheitsbegriff des § 2 Abs. 2 DesignG schließt auch Designs aus, die sich vom älteren Design nur in unwesentlichen Einzelheiten unterscheiden.",
      "Die Eigenart wird nach dem Gesamteindruck beim durchschnittlichen Verbraucher beurteilt.",
      "Der Grad der Gestaltungsfreiheit des Entwerfers beeinflusst die Eigenartsprüfung nach § 2 Abs. 3 DesignG.",
    ],
    answers: [0, 1, 3],
    explanation:
      "Zutreffend sind A, B und D: (A) § 2 Abs. 1 DesignG: Beide Voraussetzungen — Neuheit und Eigenart — sind kumulativ erforderlich. (B) § 2 Abs. 2 DesignG: Als identisch (= neuheitsschädlich) gilt auch ein Design, das sich nur in unwesentlichen Einzelheiten unterscheidet. (D) § 2 Abs. 3 S. 2 DesignG: Der Grad der Gestaltungsfreiheit des Entwerfers wird bei der Eigenartsprüfung berücksichtigt. Nicht zutreffend ist C: Die Eigenart wird nicht nach dem 'durchschnittlichen Verbraucher' (Markenrecht-Figur), sondern nach dem 'informierten Benutzer' beurteilt (§ 2 Abs. 3 DesignG) — eine eigenständige Rechtsfigur des Designrechts.",
    sourceIds: ["designG2"],
    sources: [
      { id: "designG2", label: "§ 2 DesignG", url: "https://www.gesetze-im-internet.de/geschmmg/__2.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Abgrenzung",
    skillTags: ["designrecht", "neuheit", "eigenart", "schutzvoraussetzungen"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-design-025",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "DesignG § 5 — Offenbarung",
    title: "Fall: Offenbarungsbegriff — was gilt als offenbart? (§ 5 DesignG)",
    prompt:
      "Designer D zeigt sein noch nicht angemeldetes Design exklusiv einem kleinen Kreis von Fachexperten auf einer geschlossenen Unternehmenskonferenz, ohne Vertraulichkeitsverpflichtung. Ist das Design damit im Sinne von § 5 DesignG offenbart?",
    options: [
      "Nein — eine Offenbarung setzt immer eine schriftliche Veröffentlichung oder Markteintroduktion voraus.",
      "Nein — die Präsentation vor einem kleinen, fachkundigen Kreis gilt nie als Offenbarung gegenüber der Allgemeinheit.",
      "Ja — wenn die Offenbarung an einen Kreis von Personen erfolgte, die im normalen Geschäftsverlauf des betreffenden Sektors tätig sind, wird sie als bekannt angesehen, es sei denn, eine Geheimhaltungsvereinbarung besteht.",
      "Ja — jede mündliche oder schriftliche Kommunikation über das Design außerhalb des Unternehmens gilt als Offenbarung.",
    ],
    answers: [2],
    explanation:
      "§ 5 DesignG: Bei der Beurteilung der Neuheit und Eigenart werden nur Designs berücksichtigt, die der Öffentlichkeit zugänglich gemacht worden sind. Dabei gilt: Wenn ein Design an Personen offenbart wurde, die im normalen Geschäftsverlauf des betreffenden Sektors tätig sind, und wenn keine ausdrückliche oder implizite Geheimhaltungsvereinbarung besteht, ist davon auszugehen, dass das Design im normalen Geschäftsverkehr bekannt werden konnte — also als offenbart gilt. Das Fehlen einer Vertraulichkeitsverpflichtung ist daher entscheidend: Ohne Geheimhaltung ist die Offenbarung auch in einem Fachkreis relevant.",
    sourceIds: ["designG5"],
    sources: [
      { id: "designG5", label: "§ 5 DesignG", url: "https://www.gesetze-im-internet.de/geschmmg/__5.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["designrecht", "offenbarung", "neuheit"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-design-026",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "DesignG vs. Patentrecht — Abgrenzung",
    title: "Designschutz vs. Patentschutz: Abgrenzungskriterium",
    prompt:
      "Ein Produktmerkmal hat sowohl eine ästhetische als auch eine technische Wirkung. Es gibt aber mehrere alternative Formen, die dieselbe technische Wirkung erzeugen. Ist das Merkmal vom Designschutz ausgeschlossen?",
    options: [
      "Ja — jedes Merkmal mit technischer Wirkung ist automatisch vom Designschutz ausgeschlossen.",
      "Nein — der Ausschluss nach § 3 Abs. 1 Nr. 1 DesignG greift nur, wenn das Merkmal 'ausschließlich' durch die technische Funktion bedingt ist; bestehen ästhetische Alternativen, fehlt das 'Ausschließlich'.",
      "Ja — technische und ästhetische Wirkungen schließen Designschutz gegenseitig aus.",
      "Nein — Merkmale mit technischer Wirkung können zwar nicht durch Designschutz, wohl aber durch ergänzenden Leistungsschutz geschützt werden.",
    ],
    answers: [1],
    explanation:
      "§ 3 Abs. 1 Nr. 1 DesignG: Ausgeschlossen sind Erscheinungsmerkmale, die 'ausschließlich' durch die technische Funktion bedingt sind. Wenn es mehrere Alternativformen gibt, die dieselbe technische Funktion erfüllen würden, ist die gewählte Form nicht ausschließlich durch die Funktion bedingt — der Entwerfer hatte einen gestalterischen Spielraum. Solche Merkmale können Designschutz genießen. Das Kriterium 'Ausschließlichkeit' verhindert, dass der Designschutz für technische Gestaltungen gilt, bei denen dem Entwerfer tatsächlich keine freie Wahl blieb.",
    sourceIds: ["designG3"],
    sources: [
      { id: "designG3", label: "§ 3 DesignG", url: "https://www.gesetze-im-internet.de/geschmmg/__3.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Abgrenzung",
    skillTags: ["designrecht", "ausschluesse", "technische-funktion", "abgrenzung-patent"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-design-027",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "DesignG § 38 — Schutzumfang",
    title: "Fall: Schutzumfang — Gesamteindruck des informierten Benutzers",
    prompt:
      "Inhaber I besitzt ein eingetragenes Design für einen Kaffeevollautomat mit geschwungener Front und silberfarbener Linienführung. Konkurrent K bringt einen Kaffeevollautomat mit sehr ähnlicher Front auf den Markt, der aber eine leicht abgewandelte Kontur und goldene statt silberne Akzente hat. Verletzt K Is Design?",
    options: [
      "Nein — weil die Farbe (Gold vs. Silber) einen anderen Gesamteindruck erzeugt.",
      "Es kommt darauf an, ob beim informierten Benutzer ein anderer Gesamteindruck entsteht (§ 38 Abs. 2 DesignG) — kleine Farbunterschiede und geringfügige Konturabweichungen könnten unerheblich sein, wenn die Gesamtgestalt dominiert.",
      "Ja — jede Nutzung eines ähnlichen Designs ohne Lizenz ist automatisch eine Verletzung.",
      "Nein — Da K das Design unabhängig entwickelt hat, liegt keine Verletzung vor.",
    ],
    answers: [1],
    explanation:
      "§ 38 Abs. 2 DesignG: Der Schutzumfang umfasst jedes Design, das beim informierten Benutzer keinen anderen Gesamteindruck erweckt. Die Beurteilung ist eine Gesamtbetrachtung: Einzelne Unterschiede (Farbe Gold/Silber, leicht abgewandelte Kontur) können unerheblich sein, wenn die prägende Gesamtgestalt übereinstimmt. Entscheidend ist, ob der informierte Benutzer die Produkte trotz der Unterschiede als denselben Gesamteindruck wahrnehmend bewerten würde. Unabhängige Entwicklung schützt — anders als beim nicht eingetragenen GGM — nicht vor Verletzung des eingetragenen Designs (Monopolschutz). Option A ist als Absolutaussage unrichtig.",
    sourceIds: ["designG38"],
    sources: [
      { id: "designG38", label: "§ 38 DesignG", url: "https://www.gesetze-im-internet.de/geschmmg/__38.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["designrecht", "schutzumfang", "verletzung", "gesamteindruck"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-design-028",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "DesignG § 4 — Komplexe Erzeugnisse",
    title: "Sichtbarkeitserfordernis bei Bauelementen: bei normaler vs. Wartungs-Verwendung",
    prompt:
      "Ein Ersatzteil (Bremsscheibe) ist bei normalem Fahrbetrieb eines PKW nicht sichtbar, wird aber bei Wartungsarbeiten sichtbar. Erfüllt das Bauelement das Sichtbarkeitserfordernis des § 4 DesignG?",
    options: [
      "Ja — Sichtbarkeit bei Wartungsarbeiten genügt, da auch dies zum bestimmungsgemäßen Gebrauch gehört.",
      "Nein — maßgeblich ist die Sichtbarkeit bei bestimmungsgemäßer Verwendung des komplexen Erzeugnisses (Fahren), nicht bei Reparatur oder Wartung.",
      "Ja — weil Sicherheitsteile immer designschutzfähig sein müssen.",
      "Es hängt davon ab, ob der Endverbraucher oder ein Fachmann das Teil montiert.",
    ],
    answers: [1],
    explanation:
      "§ 4 Abs. 2 DesignG: Maßgeblich ist die Sichtbarkeit bei bestimmungsgemäßer Verwendung des komplexen Erzeugnisses. Bestimmungsgemäße Verwendung eines PKW ist der Fahrbetrieb, nicht Wartung oder Reparatur. Teile, die nur bei Reparatur oder Wartung sichtbar werden, erfüllen das Sichtbarkeitserfordernis nicht. Dieses Ergebnis wurde vom EuGH (C-209/21 — Ford Motor Company) für Ersatzteile bestätigt: Sichtbarkeit nur bei Reparatur reicht für Designschutz als Bauelement nicht aus. Anders ist es bei Felgen, Stoßfängern oder Scheinwerfern, die im normalen Fahrbetrieb sichtbar bleiben.",
    sourceIds: ["designG4"],
    sources: [
      { id: "designG4", label: "§ 4 DesignG", url: "https://www.gesetze-im-internet.de/geschmmg/__4.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Abgrenzung",
    skillTags: ["designrecht", "bauelemente", "sichtbarkeit", "komplexe-erzeugnisse"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-design-029",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "multi",
    topic: "DesignG § 3 — Ausschlüsse (Überblick)",
    title: "Nicht schutzfähige Merkmale nach § 3 DesignG — mehrere Fallgruppen",
    prompt:
      "Welche der folgenden Erscheinungsmerkmale sind nach § 3 DesignG nicht schutzfähig? (Mehrere Antworten möglich)",
    options: [
      "Die Form einer Computertastatur, soweit die Tastenabmessungen zwingend zur Verbindung mit einem Standard-USB-Empfänger erforderlich sind.",
      "Die Farbe eines Produkts, die rein ästhetisch gewählt wurde.",
      "Die Form eines Klingelknopfs, die ausschließlich durch die Notwendigkeit bedingt ist, eine bestimmte elektrische Verbindung herzustellen.",
      "Das Oberflächenmuster eines Textilstoffs, das keine technische Funktion hat.",
    ],
    answers: [0, 2],
    explanation:
      "Nicht schutzfähig nach § 3 Abs. 1 Nr. 2 DesignG (must-fit) ist A: Tastenabmessungen, die zwingend zur Verbindung mit einem bestimmten Standard erforderlich sind, fallen unter den must-fit-Ausschluss. Nicht schutzfähig nach § 3 Abs. 1 Nr. 1 DesignG (technisch bedingt) ist C: Eine Form, die ausschließlich durch die Notwendigkeit einer elektrischen Verbindung bedingt ist. Schutzfähig sind dagegen B (rein ästhetisch gewählte Farbe) und D (Oberflächenmuster ohne technische Funktion) — diese fallen unter keine Ausschlussregelung des § 3 DesignG und können Neuheit und Eigenart aufweisen.",
    sourceIds: ["designG3"],
    sources: [
      { id: "designG3", label: "§ 3 DesignG", url: "https://www.gesetze-im-internet.de/geschmmg/__3.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["designrecht", "ausschluesse", "technische-funktion", "must-fit"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-design-030",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "GGV — Neuheit und Eigenart auf EU-Ebene",
    title: "Fall: GGM-Neuheit — weltweiter oder EU-weiter Formenschatz?",
    prompt:
      "Designer D möchte ein GGM beim EUIPO anmelden. Ein älteres, identisches Design wurde vor 2 Jahren ausschließlich in Japan auf einer Messe veröffentlicht, ohne dass es jemals in die EU gelangte. Ist Ds GGM-Anmeldung neuheitsschädlich betroffen?",
    options: [
      "Nein — für die GGV-Neuheit zählen nur Offenbarungen, die innerhalb der EU erfolgt sind.",
      "Ja — für die Neuheitsprüfung des GGM ist jede weltweite Offenbarung relevant, sofern sie im normalen Geschäftsverkehr der betreffenden Branche bekannt werden konnte.",
      "Ja — aber nur, wenn die Offenbarung in einem WTO-Mitgliedstaat erfolgte.",
      "Nein — Offenbarungen außerhalb der EU sind für das GGM niemals neuheitsschädlich.",
    ],
    answers: [1],
    explanation:
      "Art. 7 Abs. 1 GGV: Bei der Anwendung der Art. 5 und 6 GGV (Neuheit und Eigenart) gilt ein Design als der Öffentlichkeit zugänglich gemacht worden, wenn es bekannt gemacht, ausgestellt, im Verkehr gebracht oder auf sonstige Weise veröffentlicht wurde — ohne territoriale Begrenzung auf die EU. Entscheidend ist, ob die Offenbarung im normalen Geschäftsverlauf des betreffenden Sektors bekannt werden konnte. Eine ausschließlich in Japan gezeigte Messepräsentation in der Branche kann — wenn keine Geheimhaltung vorlag — weltweit den Formenschatz bilden. Die GGV kennt keinen auf die EU beschränkten Neuheitsbegriff.",
    sourceIds: ["ggv"],
    sources: [
      { id: "ggv", label: "GGV (CELEX:32002R0006)", url: "https://eur-lex.europa.eu/legal-content/DE/TXT/?uri=CELEX:32002R0006" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["designrecht", "ggv", "neuheit", "formenschatz"],
    reviewStatus: "reviewed",
  },

  // ─── ORDERING da-design-031 … da-design-034 ──────────────────────────────

  {
    id: "da-design-031",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "ordering",
    topic: "DesignG — Prüfungsschema Schutzvoraussetzungen",
    title: "Prüfungsschema: Designschutz-Voraussetzungsprüfung",
    prompt:
      "Bringen Sie die Prüfungsschritte der Schutzvoraussetzungsprüfung für ein eingetragenes Design in die richtige gutachtliche Reihenfolge.",
    options: [],
    answers: [],
    orderItems: [
      "1. Design (§ 1 Nr. 1 DesignG): Liegt eine Erscheinungsform eines Erzeugnisses vor?",
      "2. Erzeugnis (§ 1 Nr. 2 DesignG): Ist der Gegenstand ein Erzeugnis i.S.d. DesignG?",
      "3. Neuheit (§ 2 Abs. 2 DesignG): Ist kein identisches oder nur unwesentlich abweichendes Design offenbart?",
      "4. Eigenart (§ 2 Abs. 3 DesignG): Erzeugt das Design beim informierten Benutzer einen anderen Gesamteindruck als bekannte Designs?",
      "5. Kein Ausschluss (§ 3 DesignG): Keine ausschließlich technisch bedingten Merkmale, kein must-fit, kein ordre-public-Verstoß.",
    ],
    explanation:
      "Das gutachtliche Prüfungsschema für die Schutzfähigkeit eines Designs gliedert sich wie folgt: (1) Definition: Liegt eine Erscheinungsform vor? (§ 1 Nr. 1 DesignG). (2) Erzeugnis: Handelt es sich um einen erfassten Gegenstand? (§ 1 Nr. 2 DesignG). (3) Neuheit: Vergleich mit Formenschatz (§ 2 Abs. 2 DesignG). (4) Eigenart: Gesamteindruck beim informierten Benutzer (§ 2 Abs. 3 DesignG). (5) Keine Ausschlussgründe (§ 3 DesignG). Das DPMA prüft im Eintragungsverfahren nur formale Kriterien; Neuheit und Eigenart werden erst im Nichtigkeitsverfahren oder als Verletzungseinwand materiell geprüft.",
    sourceIds: ["designG1", "designG2", "designG3"],
    sources: [
      { id: "designG1", label: "§ 1 DesignG", url: "https://www.gesetze-im-internet.de/geschmmg/__1.html" },
      { id: "designG2", label: "§ 2 DesignG", url: "https://www.gesetze-im-internet.de/geschmmg/__2.html" },
      { id: "designG3", label: "§ 3 DesignG", url: "https://www.gesetze-im-internet.de/geschmmg/__3.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Schema",
    skillTags: ["designrecht", "pruefungsschema", "schutzvoraussetzungen"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-design-032",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "ordering",
    topic: "DesignG — Verletzungsprüfungsschema",
    title: "Prüfungsschema: Designrechtsverletzung",
    prompt:
      "Bringen Sie die Prüfungsschritte einer Designrechtsverletzung in die richtige gutachtliche Reihenfolge.",
    options: [],
    answers: [],
    orderItems: [
      "1. Bestand: Eingetragenes und noch wirksames Design des Klägers (§§ 27 ff. DesignG)?",
      "2. Verletzungshandlung: Benutzung i.S.d. § 38 Abs. 1 DesignG (Herstellung, Vertrieb, Einfuhr etc.)?",
      "3. Schutzumfang: Erweckt das angegriffene Design beim informierten Benutzer keinen anderen Gesamteindruck (§ 38 Abs. 2 DesignG)?",
      "4. Rechtsfolge: Unterlassung (§ 42 Abs. 1 DesignG), Schadensersatz bei Vorsatz/Fahrlässigkeit (§ 42 Abs. 2 DesignG).",
    ],
    explanation:
      "Gutachtliche Prüfungsreihenfolge bei Designrechtsverletzung: (1) Schutzrechtsbestand prüfen — besteht ein wirksam eingetragenes Design (nicht abgelaufen, nicht nichtig)? (2) Verletzungshandlung — nutzt der Beklagte das Design i.S.d. § 38 Abs. 1 DesignG? (3) Schutzumfang — erzeugt das angegriffene Design beim informierten Benutzer keinen anderen Gesamteindruck als das eingetragene Design? (4) Rechtsfolgen: § 42 Abs. 1 (Unterlassung) und § 42 Abs. 2 (Schadensersatz bei Verschulden).",
    sourceIds: ["designG38", "designG42"],
    sources: [
      { id: "designG38", label: "§ 38 DesignG", url: "https://www.gesetze-im-internet.de/geschmmg/__38.html" },
      { id: "designG42", label: "§ 42 DesignG", url: "https://www.gesetze-im-internet.de/geschmmg/__42.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Schema",
    skillTags: ["designrecht", "pruefungsschema", "verletzung"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-design-033",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "ordering",
    topic: "DesignG — Schutzdauer",
    title: "Schutzdauer: Chronologie der Erneuerungsschritte",
    prompt:
      "Ein Design wird am 1. Januar 2000 angemeldet. Bringen Sie die Erneuerungsschritte in die richtige zeitliche Reihenfolge bis zur maximalen Schutzdauer.",
    options: [],
    answers: [],
    orderItems: [
      "Grundlaufzeit: Schutz bis 1. Januar 2005 (5 Jahre ab Anmeldetag, § 27 Abs. 1 DesignG).",
      "1. Erneuerung: Verlängerung um 5 Jahre, Schutz bis 1. Januar 2010 (§ 27 Abs. 2 DesignG).",
      "2. Erneuerung: Verlängerung um 5 Jahre, Schutz bis 1. Januar 2015.",
      "3. Erneuerung: Verlängerung um 5 Jahre, Schutz bis 1. Januar 2020.",
      "4. Erneuerung: Verlängerung um 5 Jahre, Schutz bis 1. Januar 2025 — maximale Schutzdauer von 25 Jahren erreicht; weitere Erneuerung nicht möglich.",
    ],
    explanation:
      "§ 27 DesignG: Grundlaufzeit 5 Jahre, max. viermalige Verlängerung um je 5 Jahre = insgesamt max. 25 Jahre. Ab Anmeldung 2000: Grundlaufzeit bis 2005, nach 4 Erneuerungen läuft der Schutz bis 2025 — die absolute Obergrenze. Jede Erneuerung erfordert einen Antrag und die Zahlung der Erneuerungsgebühr (§ 28 DesignG). Wird eine Erneuerungsfrist versäumt, erlischt der Schutz; eine Wiedereinsetzung ist unter Umständen möglich.",
    sourceIds: ["designG27"],
    sources: [
      { id: "designG27", label: "§ 27 DesignG", url: "https://www.gesetze-im-internet.de/geschmmg/__27.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["designrecht", "schutzdauer", "erneuerung"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-design-034",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "ordering",
    topic: "GGV — Schutzarten im Vergleich",
    title: "Schutzarten: nationales Design, GGM eingetragen, GGM nicht eingetragen",
    prompt:
      "Bringen Sie die drei Schutzarten in aufsteigender Reihenfolge ihrer maximalen Schutzdauer.",
    options: [],
    answers: [],
    orderItems: [
      "Nicht eingetragenes GGM (Art. 11 GGV): 3 Jahre ab erster Offenbarung in der EU — kürzeste Schutzdauer.",
      "Nationales eingetragenes Design (§§ 27 f. DesignG): max. 25 Jahre ab Anmeldetag in 5-Jahres-Schritten.",
      "Eingetragenes GGM (Art. 12 GGV): max. 25 Jahre ab Anmeldetag in 5-Jahres-Schritten — gleiche Maximaldauer wie nationales Design, aber unionsweite Wirkung.",
    ],
    explanation:
      "Das nicht eingetragene GGM (Art. 11 GGV) bietet mit 3 Jahren die kürzeste Schutzdauer — geeignet für kurzlebige Produkte und als Sofortschutz. Das nationale eingetragene Design (§ 27 DesignG) und das eingetragene GGM (Art. 12 GGV) haben beide eine maximale Schutzdauer von 25 Jahren, unterscheiden sich aber im territorialen Geltungsbereich: national vs. gesamte EU. Für Unternehmen mit EU-weitem Vertrieb ist das GGM (eingetragen) daher typischerweise kosteneffizienter als parallele nationale Eintragungen in allen Mitgliedstaaten.",
    sourceIds: ["ggv", "designG27"],
    sources: [
      { id: "ggv", label: "GGV (CELEX:32002R0006)", url: "https://eur-lex.europa.eu/legal-content/DE/TXT/?uri=CELEX:32002R0006" },
      { id: "designG27", label: "§ 27 DesignG", url: "https://www.gesetze-im-internet.de/geschmmg/__27.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Abgrenzung",
    skillTags: ["designrecht", "ggv", "schutzdauer", "vergleich"],
    reviewStatus: "reviewed",
  },

  // ─── CLOZE da-design-035 … da-design-038 ─────────────────────────────────

  {
    id: "da-design-035",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "cloze",
    topic: "DesignG §§ 27-28 — Schutzdauer",
    title: "Cloze: Maximale Schutzdauer eingetragenes Design",
    prompt:
      "Die maximale Schutzdauer eines eingetragenen Designs nach dem DesignG beträgt ___ Jahre ab dem Anmeldetag, erneuerbar in Schritten von je 5 Jahren (§§ 27, 28 DesignG).",
    options: ["10", "20", "25", "70"],
    answers: [2],
    explanation:
      "§ 27 Abs. 2 DesignG: Die Schutzdauer kann durch Erneuerung um jeweils fünf Jahre verlängert werden, bis sie insgesamt 25 Jahre beträgt. Grundlaufzeit sind 5 Jahre (§ 27 Abs. 1 DesignG); nach viermaliger Verlängerung à 5 Jahre ergibt sich die maximale Gesamtlaufzeit von 25 Jahren. Zum Vergleich: Marke — unbegrenzt verlängerbar; Gebrauchsmuster — max. 10 Jahre; Patent — max. 20 Jahre.",
    sourceIds: ["designG27"],
    sources: [
      { id: "designG27", label: "§ 27 DesignG", url: "https://www.gesetze-im-internet.de/geschmmg/__27.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["designrecht", "schutzdauer"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-design-036",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "cloze",
    topic: "DesignG § 6 — Neuheitsschonfrist",
    title: "Cloze: Neuheitsschonfrist im Designrecht",
    prompt:
      "Die Neuheitsschonfrist des § 6 DesignG beträgt ___ Monate vor dem Anmeldetag (bzw. Prioritätstag).",
    options: ["3", "6", "12", "24"],
    answers: [2],
    explanation:
      "§ 6 DesignG: Die Schonfrist beträgt 12 Monate vor dem Anmeldetag. Offenbarungen des Entwerfers selbst, seiner Rechtsnachfolger oder Offenbarungen infolge einer Verletzung des Entwerferrechts innerhalb dieser 12 Monate werden bei der Neuheitsprüfung nicht berücksichtigt. Zum Vergleich: Das GGM (GGV) kennt ebenfalls eine 12-monatige Schonfrist. Das Patentrecht (§ 3 PatG) kennt grundsätzlich keine Schonfrist für Eigenoffenbarungen — hier ist die Lage deutlich restriktiver.",
    sourceIds: ["designG6"],
    sources: [
      { id: "designG6", label: "§ 6 DesignG", url: "https://www.gesetze-im-internet.de/geschmmg/__6.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["designrecht", "neuheitsschonfrist"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-design-037",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "cloze",
    topic: "GGV — nicht eingetragenes GGM",
    title: "Cloze: Schutzdauer des nicht eingetragenen GGM",
    prompt:
      "Das nicht eingetragene Gemeinschaftsgeschmacksmuster nach Art. 11 GGV entsteht automatisch mit erster Offenbarung in der EU und gewährt Schutz für ___ Jahre.",
    options: ["1", "2", "3", "5"],
    answers: [2],
    explanation:
      "Art. 11 Abs. 1 GGV: Ein nicht eingetragenes Gemeinschaftsgeschmacksmuster wird für einen Zeitraum von drei Jahren ab dem Datum geschützt, an dem es erstmals in der Gemeinschaft der Öffentlichkeit zugänglich gemacht wurde. Eine Verlängerung ist nicht möglich — nach 3 Jahren erlischt der Schutz automatisch. Zur Erinnerung: Das eingetragene GGM (Art. 12 GGV) kann bis auf 25 Jahre verlängert werden. Für Modeprodukte mit kurzer Lebenszyklusdauer ist das nicht eingetragene GGM oft ausreichend.",
    sourceIds: ["ggv"],
    sources: [
      { id: "ggv", label: "GGV (CELEX:32002R0006)", url: "https://eur-lex.europa.eu/legal-content/DE/TXT/?uri=CELEX:32002R0006" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["designrecht", "ggv", "nicht-eingetragenes-ggm", "schutzdauer"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-design-038",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "cloze",
    topic: "DesignG § 2 Abs. 3 — Eigenart",
    title: "Cloze: Prüfungsmaßstab der Eigenart",
    prompt:
      "Die Eigenart eines Designs nach § 2 Abs. 3 DesignG wird beurteilt nach dem Gesamteindruck beim ___ Benutzer.",
    options: ["durchschnittlichen", "informierten", "fachkundigen", "unbeteiligten"],
    answers: [1],
    explanation:
      "§ 2 Abs. 3 DesignG: Ein Design hat Eigenart, wenn sich der Gesamteindruck, den es beim informierten Benutzer hervorruft, von dem Gesamteindruck anderer offenbarter Designs unterscheidet. Der 'informierte Benutzer' ist die spezifische Prüfungsfigur des Designrechts: Er ist vertraut mit dem Produktsegment und dem vorhandenen Formenschatz, aber kein Designexperte. Nicht zu verwechseln mit dem 'durchschnittlichen Verbraucher' (Markenrecht) oder dem 'Fachmann' (Patentrecht).",
    sourceIds: ["designG2"],
    sources: [
      { id: "designG2", label: "§ 2 DesignG", url: "https://www.gesetze-im-internet.de/geschmmg/__2.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["designrecht", "eigenart", "informierter-benutzer"],
    reviewStatus: "reviewed",
  },

];
